/**
 * The predicate-tree accessors — "is this a group?", "what are its children?", "is this the Unknown
 * idiom?", "what are the editable atoms?" — for every side that walks an audience predicate tree: the
 * stored contract (`expression-schema.ts`), the wire mirror (`ui/src/audiences/types.ts`) and the
 * builder (`audienceBuilder/lib/tree.ts`).
 *
 * Two properties let one implementation serve all three models:
 *
 * 1. **A bottom leaf with no runtime imports.** Not even zod, so the browser can value-import it
 *    through the `@server/audience-tree` alias without dragging the schema, the compiler registry or
 *    a warehouse driver into the SPA bundle.
 * 2. **Generic in the node type, structural in what it reads.** The three models differ where they are
 *    meant to (the contract's `operator` is an `OperatorName` union, the wire mirror's a bare `string`,
 *    the builder's the `OperatorValue` menu), and the accessors read only `path` + `operator` as
 *    strings. `TSelf` carries each caller's own node type through the recursion, so `listLeaves` on a
 *    builder tree returns builder nodes.
 *
 * Two generic shapes appear below, on purpose. The non-recursive ARM READERS take
 * `PredicateNode<TNode>` — the loosest thing that can be read — so a caller whose node type allows
 * all four arms present-but-undefined (Zod's inferred shape, the wire mirror) still gets its own node
 * type back out. The RECURSIVE WALKERS take `TNode extends PredicateNode<TNode>` instead, because
 * they hand a child straight back to themselves and only the self-referential constraint proves a
 * child is walkable.
 *
 * The arm readers (`attributeOf`/`rawSqlOf`) are generic in the arm type, so each side gets its own back
 * — the contract's `AttributePredicate`, the builder's leaf. Not here: the builder's EDIT helpers
 * (`updateAt`/`removeAt`/`setGroupOp`/`pruneEmptyGroups`), which rebuild nodes, need the concrete shape,
 * and only the editing surface does it.
 */

export type AttributeArm = { path: string; operator: string };

/**
 * The structural minimum of a predicate node, self-referential in `TSelf` so a caller's own node type
 * survives the walk. Every one of the three models satisfies it: the arms are optional, so a
 * single-key union member (`{attribute: …}`) is assignable with the other three absent.
 *
 * Every arm read below tests the VALUE, never key presence: Zod keeps a present-but-undefined
 * optional key, so `'and' in node` is TRUE for `{attribute: …, and: undefined}` and reading `.map` off
 * that arm throws a TypeError out of a refinement — turning a `safeParse` into a throw.
 */
export type PredicateNode<TSelf> = CombinatorNode<TSelf> & {
  attribute?: AttributeArm | undefined;
};

/**
 * What the CHILDREN accessors read — the two combinator arms, and nothing else. `attribute`/`rawSql`
 * are named but left `unknown` on purpose: these accessors never read them, so a caller whose
 * `attribute` is some other shape (a schema's pre-refinement node, say) can still ask for children.
 * They are NAMED rather than omitted because a target of only-optional properties is a "weak type",
 * which TypeScript refuses a source that shares none of its properties — and a leaf arm shares neither
 * `and` nor `or`.
 */
export type CombinatorNode<TSelf> = {
  attribute?: unknown;
  rawSql?: unknown;
  and?: TSelf[] | undefined;
  or?: TSelf[] | undefined;
};

/** Minimum shape of a definition for {@link topLevelConjuncts} — the expression's `where`, nothing else. */
export type DefinitionWithWhere<TNode> = { expression: { where?: TNode | undefined } };

/** The other arms are named `unknown` for the weak-type reason given on {@link CombinatorNode}. */
type ArmReader<TAttribute> = { attribute?: TAttribute | undefined; rawSql?: unknown; and?: unknown; or?: unknown };

/**
 * The attribute arm, by VALUE. Generic in the arm type so each side gets its own back: the contract's
 * `AttributePredicate`, the builder's draft leaf. `null` for any node that is not an attribute leaf.
 */
export function attributeOf<TAttribute>(node: ArmReader<TAttribute>): TAttribute | null {
  return node.attribute ?? null;
}

/** The raw-SQL arm, by VALUE. See {@link attributeOf}. */
export function rawSqlOf<TRawSql>(node: {
  rawSql?: TRawSql | undefined;
  attribute?: unknown;
  and?: unknown;
  or?: unknown;
}): TRawSql | null {
  return node.rawSql ?? null;
}

export function andChildrenOf<TNode>(node: CombinatorNode<TNode>): TNode[] | null {
  return Array.isArray(node.and) ? node.and : null;
}

export function orChildrenOf<TNode>(node: CombinatorNode<TNode>): TNode[] | null {
  return Array.isArray(node.or) ? node.or : null;
}

/** The children of an `and`/`or` node, whichever arm it is; `null` for a leaf. */
export function combinatorChildrenOf<TNode>(node: CombinatorNode<TNode>): TNode[] | null {
  return andChildrenOf<TNode>(node) ?? orChildrenOf<TNode>(node);
}

export function isGroupNode<TNode>(node: CombinatorNode<TNode>): boolean {
  return combinatorChildrenOf<TNode>(node) !== null;
}

export function predicateDepth<TNode extends PredicateNode<TNode>>(node: TNode): number {
  const children = combinatorChildrenOf<TNode>(node);
  if (!children || children.length === 0) return 1;
  return 1 + Math.max(...children.map(predicateDepth));
}

/**
 * Depth-first enumeration of the editable atoms of a tree. Every `and`/`or` is descended into, so a
 * value-OR-null group is enumerated as its two arms — each is its own editable condition.
 */
export function listLeaves<TNode extends PredicateNode<TNode>>(node: TNode): TNode[] {
  const children = combinatorChildrenOf<TNode>(node);
  return children ? children.flatMap(listLeaves) : [node];
}

/**
 * The shared "what is a top-level rule" reading: the children of a root `and`, or the whole node as
 * a single conjunct. An `or` group is **never** split — truncating it mid-way changes its meaning.
 */
export function topLevelConjuncts<TNode extends PredicateNode<TNode>>(definition: DefinitionWithWhere<TNode>): TNode[] {
  const where = definition.expression.where;
  if (!where) return [];
  return andChildrenOf<TNode>(where) ?? [where];
}
